/**
 * Automations page — the broker command centre for their automated
 * workflows. Rewritten to match GHL / HubSpot expectations:
 *   - Search + status tabs + category filter
 *   - Live enable/pause toggle switch on every card (no menu drilling)
 *   - Prebuilt templates library ("start from a template")
 *   - AI composer (natural language → workflow) for custom flows
 *   - Card shows: name, category, trigger, first steps, live stats
 */
import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  Zap,
  Sparkles,
  Loader2,
  Trash2,
  Plus,
  AlertCircle,
  CalendarClock,
  Cake,
  PartyPopper,
  RefreshCw,
  ShieldCheck,
  Users,
  Search as SearchIcon,
  Copy,
  ChevronRight,
  Pencil,
  MoreVertical,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import {
  automationsApi,
  type AutomationListItem,
  type AutomationStatus,
  type AutomationCategory,
  type AutomationWorkflow,
} from "@/lib/api"
import { timeAgo } from "@shared/utils/date"
import { cn } from "@/lib/utils"
import {
  AUTOMATION_TEMPLATES,
  CATEGORY_META,
  formatTrigger,
  formatStep,
  type AutomationTemplate,
} from "@/pages/automations/templates"

const STATUS_TABS: { value: AutomationStatus | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
  { value: "paused", label: "Paused" },
  { value: "archived", label: "Archived" },
]

const CATEGORY_ICONS: Record<string, typeof Zap> = {
  Zap,
  CalendarClock,
  Cake,
  PartyPopper,
  RefreshCw,
  ShieldCheck,
  Users,
  Sparkles,
}

const EXAMPLE_PROMPTS = [
  "Send a balloon reminder 90 days before the balloon is due",
  "12 months after settlement, send a check-in email asking if they need to refinance",
  "When a new lead is created, send an SMS after 5 minutes",
  "When a deal moves to Settled, send a congratulations SMS and email",
  "If a contact hasn't been reached in 30 days, add stale-lead tag and create a call task",
]

// ============================================================
// Page
// ============================================================
export default function AutomationsPage() {
  const qc = useQueryClient()
  const [composerOpen, setComposerOpen] = useState(false)
  const [templatesOpen, setTemplatesOpen] = useState(false)
  const [runBanner, setRunBanner] = useState<string | null>(null)
  const [status, setStatus] = useState<AutomationStatus | "all">("all")
  const [category, setCategory] = useState<AutomationCategory | "all">("all")
  const [q, setQ] = useState("")

  const list = useQuery({
    queryKey: ["automations"],
    queryFn: () => automationsApi.list(),
  })

  const runDue = useMutation({
    mutationFn: () => automationsApi.runDue(),
    onSuccess: (r) => {
      setRunBanner(
        r.processed === 0
          ? "No enrolments are due to run right now."
          : `Ran ${r.processed} enrolment${r.processed === 1 ? "" : "s"}.`
      )
      qc.invalidateQueries({ queryKey: ["automations"] })
      setTimeout(() => setRunBanner(null), 5000)
    },
    onError: (err) => {
      setRunBanner(err instanceof Error ? `⚠️ ${err.message}` : "⚠️ Run failed")
      setTimeout(() => setRunBanner(null), 5000)
    },
  })

  const items = list.data?.items ?? []

  // Counts per status for the tab badges
  const statusCounts = useMemo(() => {
    const acc: Record<string, number> = { all: items.length }
    for (const it of items) {
      acc[it.status] = (acc[it.status] ?? 0) + 1
    }
    return acc
  }, [items])

  // Filtered list
  const filtered = useMemo(() => {
    return items.filter((a) => {
      if (status !== "all" && a.status !== status) return false
      if (category !== "all" && (a.category ?? "other") !== category) return false
      if (q) {
        const hay = `${a.name} ${a.description ?? ""}`.toLowerCase()
        if (!hay.includes(q.toLowerCase())) return false
      }
      return true
    })
  }, [items, status, category, q])

  const actions = (
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" onClick={() => runDue.mutate()} disabled={runDue.isPending}>
        {runDue.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
        Run due
      </Button>
      <Button variant="outline" onClick={() => setTemplatesOpen(true)}>
        <Sparkles className="h-4 w-4" />
        Templates
      </Button>
      <Button onClick={() => setComposerOpen(true)}>
        <Plus className="h-4 w-4" />
        New automation
      </Button>
    </div>
  )

  return (
    <AppShell
      title="Automations"
      subtitle="Rules that run when things happen in the CRM"
      actions={actions}
    >
      <div className="space-y-4">
        {runBanner && (
          <div className="border-blue-200 bg-blue-50 rounded-md border p-3 text-sm text-blue-900">
            {runBanner}
          </div>
        )}

        {/* Search + status tabs */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-[380px]">
            <SearchIcon className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search automations…"
              className="pl-9"
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {STATUS_TABS.map((t) => {
              const n = statusCounts[t.value] ?? 0
              const active = status === t.value
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setStatus(t.value)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
                    active
                      ? "border-rc-blue-600 bg-rc-blue-600 text-white"
                      : "border-border bg-background text-muted-foreground hover:border-rc-blue-300 hover:text-foreground"
                  )}
                >
                  {t.label}
                  <span
                    className={cn(
                      "rounded px-1.5 text-[10px]",
                      active ? "bg-white/20" : "bg-rc-grey-100 text-muted-foreground"
                    )}
                  >
                    {n}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-1">
          <CategoryChip label="All categories" active={category === "all"} onClick={() => setCategory("all")} />
          {(Object.keys(CATEGORY_META) as AutomationCategory[]).map((cat) => (
            <CategoryChip
              key={cat}
              label={CATEGORY_META[cat].label}
              active={category === cat}
              onClick={() => setCategory(cat)}
            />
          ))}
        </div>

        {/* Results */}
        {list.isLoading ? (
          <Card className="grid place-items-center p-16">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </Card>
        ) : filtered.length === 0 ? (
          <EmptyState
            hasAnyAutomations={items.length > 0}
            onCompose={() => setComposerOpen(true)}
            onTemplates={() => setTemplatesOpen(true)}
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">
            {filtered.map((a) => (
              <AutomationCard key={a.id} automation={a} />
            ))}
          </div>
        )}
      </div>

      <ComposerDialog open={composerOpen} onOpenChange={setComposerOpen} />
      <TemplatesDialog open={templatesOpen} onOpenChange={setTemplatesOpen} />
    </AppShell>
  )
}

// ============================================================
// Category chip
// ============================================================
function CategoryChip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
        active
          ? "border-rc-blue-500 bg-rc-blue-50 text-rc-blue-700"
          : "border-border bg-background text-muted-foreground hover:border-rc-blue-300 hover:text-foreground"
      )}
    >
      {label}
    </button>
  )
}

// ============================================================
// Empty state
// ============================================================
function EmptyState({
  hasAnyAutomations,
  onCompose,
  onTemplates,
}: {
  hasAnyAutomations: boolean
  onCompose: () => void
  onTemplates: () => void
}) {
  return (
    <Card className="p-12 text-center">
      <div className="bg-rc-blue-50 text-rc-blue-600 mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full">
        <Zap className="h-7 w-7" />
      </div>
      <p className="text-sm font-medium text-foreground">
        {hasAnyAutomations ? "Nothing matches these filters" : "No automations yet"}
      </p>
      <p className="text-muted-foreground mt-1 max-w-md mx-auto text-xs">
        {hasAnyAutomations
          ? "Try clearing filters or picking a different category."
          : "Automations run rules like \"when X happens, do Y\". Start from a template or describe what you want."}
      </p>
      {!hasAnyAutomations && (
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <Button variant="outline" onClick={onTemplates}>
            <Sparkles className="h-4 w-4" />
            Browse templates
          </Button>
          <Button onClick={onCompose}>
            <Plus className="h-4 w-4" />
            Build with AI
          </Button>
        </div>
      )}
    </Card>
  )
}

// ============================================================
// Automation card
// ============================================================
function AutomationCard({ automation }: { automation: AutomationListItem }) {
  const qc = useQueryClient()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const wf = useMemo<AutomationWorkflow | null>(() => {
    try {
      return JSON.parse(automation.workflow) as AutomationWorkflow
    } catch {
      return null
    }
  }, [automation.workflow])

  // Live enrolment counts — mirrors the detail page so the two views agree.
  // Falls back to zero if the request hasn't loaded yet.
  const runsQuery = useQuery({
    queryKey: ["automation-runs", automation.id],
    queryFn: () => automationsApi.runs(automation.id),
    staleTime: 30_000,
  })
  const runs = runsQuery.data?.items ?? []
  const activeCount = runs.filter((r) =>
    ["queued", "running", "waiting"].includes(r.status)
  ).length
  const completedCount = runs.filter(
    (r) => r.status === "completed" || r.status === "exited"
  ).length
  const failedCount = runs.filter((r) => r.status === "failed").length

  const activate = useMutation({
    mutationFn: () => automationsApi.activate(automation.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["automations"] }),
    onError: (e) => setError(e instanceof Error ? e.message : "Failed"),
  })
  const pause = useMutation({
    mutationFn: () => automationsApi.pause(automation.id, "paused"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["automations"] }),
    onError: (e) => setError(e instanceof Error ? e.message : "Failed"),
  })
  const remove = useMutation({
    mutationFn: () => automationsApi.remove(automation.id),
    onSuccess: () => {
      setConfirmDelete(false)
      qc.invalidateQueries({ queryKey: ["automations"] })
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Failed"),
  })

  const isActive = automation.status === "active"
  const isArchived = automation.status === "archived"
  const category = (automation.category ?? "other") as AutomationCategory
  const catMeta = CATEGORY_META[category]

  const trigger = wf ? formatTrigger(wf.trigger.kind, wf.trigger.params ?? {}) : "—"
  const stepSummary = wf ? wf.steps.slice(0, 3).map((s) => formatStep(s)) : []
  const stepOverflow = wf && wf.steps.length > 3 ? wf.steps.length - 3 : 0

  return (
    <Card className="flex flex-col p-4">
      {/* Header */}
      <div className="flex items-start gap-2">
        <div className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-md text-base", catMeta.color)}>
          {(() => {
            const Icon = CATEGORY_ICONS[catMeta.icon] ?? Sparkles
            return <Icon className="h-4 w-4" />
          })()}
        </div>
        <div className="min-w-0 flex-1">
          <Link
            to={`/automations/${automation.id}`}
            className="hover:text-rc-blue-700 block truncate text-sm font-semibold text-foreground"
          >
            {automation.name}
          </Link>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
            <Badge variant="muted" className="text-[10px]">
              {catMeta.label}
            </Badge>
            <StatusBadge status={automation.status} />
          </div>
        </div>
        {/* Toggle: only for non-archived */}
        {!isArchived && (
          <ToggleSwitch
            checked={isActive}
            disabled={activate.isPending || pause.isPending}
            onChange={(next) => {
              setError(null)
              if (next) activate.mutate()
              else pause.mutate()
            }}
            aria-label={isActive ? "Pause automation" : "Activate automation"}
          />
        )}
      </div>

      {/* Description */}
      {automation.description && (
        <p className="text-muted-foreground mt-2 line-clamp-2 text-xs">
          {automation.description}
        </p>
      )}

      {/* Trigger */}
      <div className="mt-3 rounded-md border border-border bg-rc-grey-50 px-3 py-2">
        <div className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wide">
          Trigger
        </div>
        <div className="mt-0.5 text-xs font-medium text-foreground">{trigger}</div>
      </div>

      {/* Steps summary */}
      {stepSummary.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
          {stepSummary.map((s, i) => (
            <span key={i} className="inline-flex items-center gap-1">
              <span className="rounded bg-rc-grey-100 px-1.5 py-0.5 text-foreground">{s}</span>
              {i < stepSummary.length - 1 && <ChevronRight className="h-3 w-3" />}
            </span>
          ))}
          {stepOverflow > 0 && (
            <span className="text-muted-foreground text-[10px]">+{stepOverflow} more</span>
          )}
        </div>
      )}

      {/* Stats + last activity */}
      <div className="border-border mt-3 flex items-center justify-between border-t pt-3 text-xs">
        <div className="flex gap-3">
          <Stat label="Active" value={activeCount} tone="blue" />
          <Stat label="Completed" value={completedCount} tone="emerald" />
          <Stat label="Failed" value={failedCount} tone="red" />
        </div>
        <div className="text-muted-foreground text-[11px]">
          Updated {timeAgo(automation.updatedAt)}
        </div>
      </div>

      {/* Errors */}
      {error && (
        <div className="border-destructive/40 bg-destructive/5 text-destructive mt-2 rounded-md border p-2 text-xs">
          {error}
        </div>
      )}

      {/* Row actions */}
      <div className="border-border mt-3 flex items-center justify-between gap-2 border-t pt-2">
        <Link
          to={`/automations/${automation.id}`}
          className="text-rc-blue-600 hover:text-rc-blue-700 inline-flex items-center gap-1 text-xs font-medium"
        >
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </Link>
        <button
          type="button"
          className="text-muted-foreground hover:text-destructive p-1.5 text-xs"
          title="Delete"
          onClick={() => setConfirmDelete(true)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Delete confirm */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete “{automation.name}”?</DialogTitle>
            <DialogDescription>
              This will stop the automation permanently and remove its history.
              You can't undo this.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              {remove.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

function StatusBadge({ status }: { status: AutomationStatus }) {
  const map: Record<AutomationStatus, { label: string; className: string }> = {
    active: { label: "Active", className: "bg-emerald-100 text-emerald-700" },
    paused: { label: "Paused", className: "bg-amber-100 text-amber-700" },
    draft: { label: "Draft", className: "bg-slate-100 text-slate-700" },
    archived: { label: "Archived", className: "bg-rc-grey-100 text-rc-grey-700" },
  }
  const meta = map[status]
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium",
        meta.className
      )}
    >
      {meta.label}
    </span>
  )
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone?: "emerald" | "red" | "blue"
}) {
  return (
    <div className="flex flex-col">
      <span
        className={cn(
          "text-sm font-semibold",
          tone === "emerald" && value > 0
            ? "text-emerald-600"
            : tone === "red" && value > 0
              ? "text-destructive"
              : tone === "blue" && value > 0
                ? "text-rc-blue-600"
                : "text-foreground"
        )}
      >
        {value}
      </span>
      <span className="text-muted-foreground text-[10px] uppercase tracking-wide">
        {label}
      </span>
    </div>
  )
}

// ============================================================
// Toggle switch (proper look, keyboard accessible)
// ============================================================
function ToggleSwitch({
  checked,
  disabled,
  onChange,
  ...rest
}: {
  checked: boolean
  disabled?: boolean
  onChange: (next: boolean) => void
  "aria-label"?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={cn(
        "focus-visible:ring-ring/40 relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2",
        checked ? "bg-rc-blue-600" : "bg-rc-grey-300",
        disabled && "opacity-50 cursor-not-allowed"
      )}
      {...rest}
    >
      <span
        className={cn(
          "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
          checked ? "translate-x-[18px]" : "translate-x-0.5"
        )}
      />
    </button>
  )
}

// ============================================================
// Composer dialog (natural language → workflow)
// ============================================================
function ComposerDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const qc = useQueryClient()
  const [prompt, setPrompt] = useState("")
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [category, setCategory] = useState<AutomationCategory>("other")
  const [draft, setDraft] = useState<{
    workflow: AutomationWorkflow
    aiNotes: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const compose = useMutation({
    mutationFn: (p: string) => automationsApi.compose(p),
    onSuccess: (r) => {
      setError(null)
      if (r.ok) {
        setDraft({ workflow: r.workflow, aiNotes: r.aiNotes })
        if (!name) {
          const kind = r.workflow.trigger.kind
          setName(`When ${kind.replace(/_/g, " ")}`)
        }
        if (category === "other") {
          setCategory(guessCategory(r.workflow.trigger.kind))
        }
      } else {
        setError(r.message)
      }
    },
    onError: (e) => setError(e instanceof Error ? e.message : "AI compose failed"),
  })

  const save = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error("No draft to save")
      return automationsApi.create({
        name: name.trim() || "Untitled automation",
        description: description.trim() || null,
        workflow: draft.workflow,
        prompt,
        aiNotes: draft.aiNotes,
        category,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["automations"] })
      reset()
      onOpenChange(false)
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Save failed"),
  })

  const reset = () => {
    setPrompt("")
    setName("")
    setDescription("")
    setDraft(null)
    setCategory("other")
    setError(null)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset()
        onOpenChange(o)
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="text-rc-blue-600 h-4 w-4" />
            New automation
          </DialogTitle>
          <DialogDescription>
            Describe what you want in plain English. AI turns it into a workflow you can review, tweak, and save.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>What should happen?</Label>
            <Textarea
              rows={3}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. Send a balloon reminder 90 days before the balloon is due"
              autoFocus
            />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {EXAMPLE_PROMPTS.map((ex, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPrompt(ex)}
                  className="text-rc-blue-700 hover:bg-rc-blue-50 rounded border border-rc-blue-200 bg-rc-blue-50/40 px-2 py-0.5 text-[11px] transition-colors"
                >
                  {ex.length > 80 ? ex.slice(0, 80) + "…" : ex}
                </button>
              ))}
            </div>
            <div className="mt-3 flex justify-end">
              <Button
                type="button"
                onClick={() => compose.mutate(prompt)}
                disabled={prompt.trim().length < 6 || compose.isPending}
              >
                {compose.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                Draft with AI
              </Button>
            </div>
          </div>

          {error && (
            <div className="border-destructive/40 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border p-2.5 text-sm">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {draft && (
            <WorkflowPreview
              workflow={draft.workflow}
              aiNotes={draft.aiNotes}
              name={name}
              setName={setName}
              description={description}
              setDescription={setDescription}
              category={category}
              setCategory={setCategory}
            />
          )}
        </div>

        <div className="border-border mt-4 flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {draft && (
            <>
              <Button variant="outline" onClick={() => compose.mutate(prompt)}>
                Regenerate
              </Button>
              <Button onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                Save as draft
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function guessCategory(kind: string): AutomationCategory {
  if (kind === "birthday") return "birthday"
  if (kind === "balloon_due") return "balloon"
  if (kind === "settlement_anniversary") return "settlement"
  if (kind === "days_since_deal_lost" || kind === "days_since_last_contact") return "re_engagement"
  if (kind === "contact_created" || kind === "deal_created") return "lead_nurture"
  if (kind === "deal_stage_changed") return "settlement"
  return "other"
}

// ============================================================
// Workflow preview inside composer
// ============================================================
function WorkflowPreview({
  workflow,
  aiNotes,
  name,
  setName,
  description,
  setDescription,
  category,
  setCategory,
}: {
  workflow: AutomationWorkflow
  aiNotes: string
  name: string
  setName: (s: string) => void
  description: string
  setDescription: (s: string) => void
  category: AutomationCategory
  setCategory: (c: AutomationCategory) => void
}) {
  return (
    <div className="border-border bg-rc-grey-50/50 rounded-lg border p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles className="text-rc-blue-600 h-4 w-4" />
        <span className="text-sm font-semibold text-foreground">AI draft</span>
      </div>

      <div>
        <Label>Name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Automation name" />
      </div>

      <div>
        <Label>
          Description <span className="text-muted-foreground text-xs">(optional)</span>
        </Label>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What this automation does"
        />
      </div>

      <div>
        <Label>Category</Label>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {(Object.keys(CATEGORY_META) as AutomationCategory[]).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              className={cn(
                "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                category === c
                  ? "border-rc-blue-500 bg-rc-blue-50 text-rc-blue-700"
                  : "border-border bg-background text-muted-foreground hover:border-rc-blue-300"
              )}
            >
              {CATEGORY_META[c].label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-1">
        <Label>Trigger</Label>
        <div className="rounded-md border border-border bg-white px-3 py-2 text-sm">
          {formatTrigger(workflow.trigger.kind, workflow.trigger.params ?? {})}
        </div>
      </div>

      <div className="space-y-1">
        <Label>Steps ({workflow.steps.length})</Label>
        <ol className="space-y-1">
          {workflow.steps.map((s, i) => (
            <li
              key={s.id}
              className="rounded-md border border-border bg-white px-3 py-1.5 text-sm flex items-center gap-2"
            >
              <span className="bg-rc-blue-50 text-rc-blue-700 grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] font-bold">
                {i + 1}
              </span>
              <span className="text-foreground">{formatStep(s)}</span>
            </li>
          ))}
        </ol>
      </div>

      {aiNotes && (
        <div className="border-amber-200 bg-amber-50 text-amber-900 rounded-md border p-2 text-xs">
          <strong>AI notes:</strong> {aiNotes}
        </div>
      )}
    </div>
  )
}

// ============================================================
// Templates dialog
// ============================================================
function TemplatesDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const qc = useQueryClient()
  const [selectedCat, setSelectedCat] = useState<AutomationCategory | "all">("all")
  const [copied, setCopied] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const useTemplate = useMutation({
    mutationFn: (tpl: AutomationTemplate) =>
      automationsApi.create({
        name: tpl.name,
        description: tpl.description,
        workflow: tpl.workflow,
        category: tpl.category,
      }),
    onSuccess: (_r, tpl) => {
      setCopied(tpl.id)
      qc.invalidateQueries({ queryKey: ["automations"] })
      setTimeout(() => setCopied(null), 2500)
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Failed to create automation"),
  })

  const templates = useMemo(
    () =>
      selectedCat === "all"
        ? AUTOMATION_TEMPLATES
        : AUTOMATION_TEMPLATES.filter((t) => t.category === selectedCat),
    [selectedCat]
  )

  const categoriesInUse = useMemo(() => {
    const s = new Set<AutomationCategory>()
    for (const t of AUTOMATION_TEMPLATES) s.add(t.category)
    return Array.from(s)
  }, [])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="text-rc-blue-600 h-4 w-4" />
            Automation templates
          </DialogTitle>
          <DialogDescription>
            Start from a proven broker workflow. Click a template to add it as a draft — you can tweak the copy before activating.
          </DialogDescription>
        </DialogHeader>

        {/* Category filter */}
        <div className="flex flex-wrap gap-1">
          <CategoryChip
            label="All"
            active={selectedCat === "all"}
            onClick={() => setSelectedCat("all")}
          />
          {categoriesInUse.map((c) => (
            <CategoryChip
              key={c}
              label={CATEGORY_META[c].label}
              active={selectedCat === c}
              onClick={() => setSelectedCat(c)}
            />
          ))}
        </div>

        {error && (
          <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">
            {error}
          </div>
        )}

        {/* Grid */}
        <div className="grid max-h-[60vh] grid-cols-1 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
          {templates.map((tpl) => {
            const catMeta = CATEGORY_META[tpl.category]
            const Icon = CATEGORY_ICONS[tpl.icon] ?? Sparkles
            const done = copied === tpl.id
            return (
              <div
                key={tpl.id}
                className="border-border hover:border-rc-blue-400 rounded-md border p-3 transition-colors"
              >
                <div className="flex items-start gap-2">
                  <div className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-md", catMeta.color)}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <div className="truncate text-sm font-semibold text-foreground">{tpl.name}</div>
                      <Badge variant="muted" className="text-[10px]">
                        {catMeta.label}
                      </Badge>
                    </div>
                    <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">{tpl.description}</p>
                    <div className="text-muted-foreground mt-1.5 flex flex-wrap gap-1 text-[10px]">
                      <span className="rounded bg-rc-grey-100 px-1.5 py-0.5">
                        {formatTrigger(tpl.workflow.trigger.kind, tpl.workflow.trigger.params ?? {})}
                      </span>
                      <span className="text-muted-foreground">·</span>
                      <span>{tpl.workflow.steps.length} step{tpl.workflow.steps.length === 1 ? "" : "s"}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  {done ? (
                    <span className="text-emerald-600 inline-flex items-center gap-1 text-xs font-medium">
                      <Copy className="h-3 w-3" /> Added as draft
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => useTemplate.mutate(tpl)}
                      disabled={useTemplate.isPending}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Use template
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <div className="border-border flex items-center justify-between border-t pt-3">
          <p className="text-muted-foreground text-xs">
            New drafts appear paused. Toggle them on when ready.
          </p>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

void MoreVertical
